import { useEffect } from 'react'
import { HYBRID_BENEFIT_LABEL, RDS_GOLDEN_IMAGE, SECURITY_TYPE_LABEL } from '@/data/dedicatedServers'
import { rdsPublicIpCoverage } from '@/data/publicIp'
import { useExistingServers } from '@/hooks/useInfra'
import { useVmSizeCheck } from '@/hooks/useVmSizeCheck'
import { lookupErrorMessage } from '@/services/infraLive'
import { useDeploymentWizard } from '@/stores/deploymentWizard'
import { effectiveServerMode, offersExistingServer, rdsVmCount, useInfrastructureWizard } from '@/stores/infrastructureWizard'
import { rdsServerDetails } from '@/utils/existingServers'
import { hasNamingRule } from '@/utils/naming'
import { usesPoolTiers } from '@/utils/sizing'
import styles from './Infrastructure.module.css'
import { DataDiskEditor } from './DataDiskEditor'
import { ExistingServerSelect } from './ExistingServerSelect'
import { NetworkSelect } from './NetworkSelect'
import { PublicIpQuestion } from './PublicIpQuestion'
import { RdsSetCheck } from './RdsSetCheck'
import { RdsVmCountField } from './RdsVmCountField'
import { ReadOnlyInfoCard } from './ReadOnlyInfoCard'
import { ServerChoice } from './ServerChoice'
import { ServerNameCard } from './ServerNameCard'
import { VmSpecCard } from './VmSpecCard'

/**
 * The RDS server, on either level: Existing (one of the RDS servers of the chosen environment, region and resource group) or
 * Dedicated (new ones: how many RDS VMs, their generated names, the one golden image, its Trusted launch security, a VNet and subnet with
 * room for all of them, whether they get a public IP (one answer for the whole set), data disks, the fixed RDS VM with the pool tier chosen in
 * Configure Sizes, and whether the whole set can be created).
 * Process Pro Global's is Existing or Dedicated too. The number of RDS VMs is asked of the products that use the pool tiers only (Traverse,
 * Process Pro Global); it is the one number the names, the subnet's free IPs and the VM quota check follow.
 */
export function RdsServerSection() {
  const product = useInfrastructureWizard((s) => s.product)
  const environment = useInfrastructureWizard((s) => s.environment)
  const region = useInfrastructureWizard((s) => s.region)
  const resourceGroup = useInfrastructureWizard((s) => s.resourceGroup)
  const rds = useInfrastructureWizard((s) => s.rdsServer)
  const setMode = useInfrastructureWizard((s) => s.setRdsMode)
  const select = useInfrastructureWizard((s) => s.selectExistingRds)
  const patch = useInfrastructureWizard((s) => s.patchRdsServer)
  const keepServers = useInfrastructureWizard((s) => s.setExistingRdsServers)
  const setPublicIp = useInfrastructureWizard((s) => s.setRdsPublicIp)
  const poolTier = useDeploymentWizard((s) => s.poolTier)
  const count = useInfrastructureWizard((s) => rdsVmCount(s))
  const sizes = useVmSizeCheck(count)
  const mode = effectiveServerMode(product, rds.mode, 'rds')
  const where = { environmentId: environment?.id ?? '', regionId: region?.id ?? '', resourceGroupId: resourceGroup?.id ?? '' }
  const servers = useExistingServers(product, where.environmentId, where.regionId, where.resourceGroupId, mode === 'existing')

  // The validators read the servers that exist from the store, so what was fetched is kept there.
  useEffect(() => {
    if (servers.data) keepServers(servers.data.rdsServers)
  }, [servers.data, keepServers])

  if (!product) return null
  return (
    <section className={styles.section} aria-label="RDS server">
      <h4 className={styles.sectionTitle}>RDS server</h4>
      <ServerChoice
        server="RDS server"
        name="rds-server-mode"
        value={mode}
        onChange={setMode}
        dedicatedOnly={!offersExistingServer(product, 'rds')}
        existingDescription="Use an RDS server that already exists in this resource group."
        dedicatedDescription="Create a new RDS server for this customer."
      />

      {mode === 'existing' && (
        <ExistingServerSelect
          id="rds-existing"
          label="Existing RDS server"
          servers={servers.data?.rdsServers}
          value={rds.existing}
          onChange={select}
          isLoading={servers.isLoading}
          isError={servers.isError}
          errorMessage={lookupErrorMessage(servers.error) ?? 'Could not load the RDS servers of this resource group.'}
          onRetry={() => void servers.refetch()}
          emptyMessage={`No existing RDS server found in ${resourceGroup?.name ?? ''} / ${region?.label ?? ''}.`}
          details={(server) => rdsServerDetails(server, region?.label ?? '')}
        />
      )}

      {mode === 'dedicated' && (
        <>
          {usesPoolTiers(product) && <RdsVmCountField />}
          {hasNamingRule(product) && <ServerNameCard role="rds" idPrefix="rds-dedicated-name" count={count} />}
          <ReadOnlyInfoCard
            title="Golden image"
            items={[
              { label: 'Image', value: RDS_GOLDEN_IMAGE.label, mono: true },
              { label: 'Gallery', value: RDS_GOLDEN_IMAGE.gallery, mono: true },
              { label: 'Security type', value: SECURITY_TYPE_LABEL },
              { label: HYBRID_BENEFIT_LABEL, value: 'Enabled' },
            ]}
            note="Every dedicated RDS server is built from this one image, pinned to this version, with Trusted launch. There is no image or operating system to choose."
          />
          <NetworkSelect product={product} {...where} value={rds.network} onChange={(network) => patch({ network })} requiredIps={count} label="RDS server network" idPrefix="rds-dedicated-net" />
          {/* Asked of the products the Traverse pipeline builds only: it is the one that reads the answer (a Ross server is built by its own). */}
          {usesPoolTiers(product) && <PublicIpQuestion name="rds-public-ip" value={rds.publicIp} onChange={setPublicIp} coverage={rdsPublicIpCoverage(count, rds.publicIp)} />}
          <DataDiskEditor disks={rds.disks} onChange={(disks) => patch({ disks })} label="RDS server" idPrefix="rds-dedicated" />
          {usesPoolTiers(product) && (
            <>
              <VmSpecCard
                kind="rds"
                poolTier={poolTier}
                title="RDS VM size"
                note="Every RDS VM is this size, whatever the pool tier is. The pool tier chosen in Configure Sizes sets the sessions. Neither can be set here."
                check={sizes.rds}
                checkNote={sizes.note}
              />
              <RdsSetCheck />
            </>
          )}
        </>
      )}
    </section>
  )
}
